import { HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ClientProcessingError } from 'app/_errors/client-processing.error';
import { UnknownApplicationError } from 'app/_errors/unknown-application.error';
import { QueryStringSearch } from 'app/_models/query-string-search.model';
import { LATEST_ONLY_HEADER, LatestOnlyScope } from 'app/_interceptors/latest-only-http.interceptor';
import { PaginatedResponse } from 'app/_models/paginated-data.model';
import { Pagination } from 'app/_models/pagination.model';
import { Observable } from 'rxjs-compat';
import { throwError } from 'rxjs';
import { StrictHttpParamCodec } from './http-param-codec';

@Injectable()
export abstract class BaseHttpClientService {

  constructor() { }

  protected createQueryStrings(pagination?: Pagination, sort?: string[], search?: QueryStringSearch[]) {
    let params = this.createHttpParams();

    if (pagination) {
      params = params.append('page', String(pagination.page));
      params = params.append('size', String(pagination.size));
    }

    if (sort) {
      sort.forEach(sortPart => {
        params = params.append('sort', sortPart);
      });
    }

    if (search) {
      search
          .filter(searchPart => searchPart.value !== undefined && searchPart.value !== null && searchPart.value !== '')
          .forEach(searchPart => {
            params = params.append(searchPart.key, searchPart.value);
          });
    }

    return params;
  }

  protected createHttpParams() {
    return new HttpParams({ encoder: new StrictHttpParamCodec() });
  }

  // pass the response, and the model it should be mapped to
  protected handleData<Type>(response: HttpResponse<any>, responseModel: { new(): Type }): Type {
    return new responseModel()['deserialize'](response);
  }

  protected handlePaginatedData<Type>(response: HttpResponse<any>, responseModel?: { new(): Type }): PaginatedResponse<Type> {
    // find total count in the headers
    const totalRecords = Number(response.headers.get('X-Total-Count'));
    // map each item in the array to the model

    let body;
    if (responseModel) {
      body = response.body.map(item => new responseModel()['deserialize'](item));
    } else {
      body = response.body;
    }

    return {
      totalRecords,
      body,
    };
  }

  protected handleError(error: any) {
    // Problem with connection or HTTP error response.
    if (error instanceof HttpErrorResponse) {
      return throwError(error);
    }

    // Application code problem while handling a server response.
    if (error instanceof Error) {
      return throwError(
        new ClientProcessingError('The application could not process the server response.', error)
      );
    }

    // Unknown application code problem.
    return throwError(
      new UnknownApplicationError('The application threw an unknown error value.', error)
    );
  }

  protected applyLatestOnlyHeader(existingHeaders: HttpHeaders = new HttpHeaders(), scope: LatestOnlyScope = 'path'): HttpHeaders {
    return existingHeaders.set(LATEST_ONLY_HEADER, scope);
  }
}
